import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSpa, FaBolt, FaRegGem, FaMagic } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import OrganicPattern from './Patterns';
import pricelistData from '../data/pricelistData.json';
import type { PriceItem, TieredPriceItem, LaserPackage } from '../data/pricelistTypes';

type Category = {
  icon: IconType;
  title: string;
  subtitle?: string;
  items: PriceItem[];
  laserItems?: TieredPriceItem[];
  laserPackages?: LaserPackage[];
};

type Categories = {
  [key: string]: Category;
};

const PriceList = () => {
  const [activeCategory, setActiveCategory] = useState<string>('facials');

  const formatPrice = (price?: number | string) => {
    if (price === undefined || price === null) return '—';
    return typeof price === 'number' ? price.toLocaleString() : price;
  };

  const categories: Categories = {
    facials: { icon: FaSpa, ...pricelistData.facials },
    peels: { icon: FaMagic, ...pricelistData.peels },
    laser: {
      icon: FaBolt,
      title: pricelistData.laser.title,
      subtitle: pricelistData.laser.subtitle,
      items: [],
      laserItems: pricelistData.laser.items,
      laserPackages: pricelistData.laser.packages,
    },
    spa: { icon: FaRegGem, ...pricelistData.spa },
  };

  const renderSimpleTable = (items: PriceItem[]) => {
    let lastGroup: string | undefined;

    return (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="bg-lapis-lazuli text-white">
              <th className="text-left py-3 px-4 font-bodoni font-medium rounded-tl-lg">Treatment</th>
              <th className="text-right py-3 px-4 font-bodoni font-medium rounded-tr-lg">Price</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const showGroupHeader = item.group && item.group !== lastGroup;
              if (item.group) lastGroup = item.group;

              return (
                <React.Fragment key={`${item.group ?? 'item'}-${item.name}`}>
                  {showGroupHeader && (
                    <tr>
                      <td
                        colSpan={2}
                        className="pt-5 pb-2 px-4 font-bodoni text-lg text-lapis-lazuli border-b border-lapis-lazuli/20 bg-white"
                      >
                        {item.group}
                      </td>
                    </tr>
                  )}
                  <motion.tr
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-3 px-4 text-gray-900">
                      <span className="font-medium block">{item.name}</span>
                      {item.duration && (
                        <span className="text-sm text-gray-500">{item.duration}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right text-lapis-lazuli font-medium whitespace-nowrap">
                      {item.price !== undefined
                        ? `KES ${formatPrice(item.price)}`
                        : 'Contact for pricing'}
                    </td>
                  </motion.tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderLaserContent = () => {
    const { laserItems, laserPackages } = categories.laser;
    if (!laserItems) return null;

    return (
      <div className="space-y-10">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="bg-lapis-lazuli text-white">
                <th className="text-left py-3 px-4 font-bodoni font-medium rounded-tl-lg">Treatment Area</th>
                <th className="text-right py-3 px-3 font-bodoni font-medium whitespace-nowrap">Hers</th>
                <th className="text-right py-3 px-3 font-bodoni font-medium whitespace-nowrap">His</th>
                <th className="text-right py-3 px-4 font-bodoni font-medium whitespace-nowrap rounded-tr-lg">Under 16</th>
              </tr>
            </thead>
            <tbody>
              {laserItems.map((item, index) => (
                <motion.tr
                  key={item.name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="py-3 pr-4 text-gray-900">{item.name}</td>
                  <td className="py-3 px-3 text-right text-lapis-lazuli font-medium whitespace-nowrap">
                    {item.hers !== undefined ? `KES ${item.hers.toLocaleString()}` : '—'}
                  </td>
                  <td className="py-3 px-3 text-right text-lapis-lazuli font-medium whitespace-nowrap">
                    {item.his !== undefined ? `KES ${item.his.toLocaleString()}` : '—'}
                  </td>
                  <td className="py-3 pl-3 text-right text-lapis-lazuli font-medium whitespace-nowrap">
                    {item.under16 !== undefined ? `KES ${item.under16.toLocaleString()}` : '—'}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {laserPackages && (
          <div>
            <h3 className="font-bodoni text-xl text-lapis-lazuli mb-4">Session Packages</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              {laserPackages.map((pkg, index) => (
                <motion.div
                  key={pkg.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-4 rounded-lg border border-lapis-lazuli/20 bg-lapis-lazuli/5"
                >
                  <h4 className="font-medium text-gray-900 mb-1">{pkg.title}</h4>
                  <p className="text-sm text-gray-600 mb-2">{pkg.areas}</p>
                  <p className="text-lapis-lazuli font-medium">{pkg.offer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white">
      <div className="relative h-[35vh] md:h-[40vh] bg-eerie-black overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url("/api/placeholder/1920/1080")' }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-eerie-black/90 via-eerie-black/80 to-eerie-black/70" />
        </div>

        <OrganicPattern />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative h-full flex items-center justify-center text-center px-4 pt-12 md:pt-16"
        >
          <div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="font-magenta text-3xl md:text-5xl lg:text-7xl text-timber-wolf mb-2 md:mb-4"
            >
              Services & Pricing
            </motion.h1>
      <motion.p 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.4 }}
        className="font-bodoni text-base md:text-xl text-timber-wolf/80"
      >
        Luxury treatments for every need
      </motion.p>
      <motion.a
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.6 }}
        href={`${process.env.PUBLIC_URL}/Studio-Omorfia-Price-List.pdf`}
        download
        className="inline-block mt-6 px-6 py-2.5 border border-timber-wolf/40 text-timber-wolf font-bodoni text-sm tracking-wide hover:bg-timber-wolf/10 transition-colors rounded-md"
      >
        Download Price List (PDF)
      </motion.a>
          </div>
        </motion.div>
      </div>

      <div className="max-w-6xl mx-auto py-20 px-4 bg-white">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {Object.entries(categories).map(([key, category]) => (
            <button
              key={key}
              onClick={() => setActiveCategory(key)}
              className={`p-4 rounded-lg transition-all ${
                activeCategory === key
                  ? 'bg-lapis-lazuli text-white'
                  : 'bg-timber-wolf text-lapis-lazuli hover:bg-lapis-lazuli/10'
              }`}
            >
              <category.icon className="w-6 h-6 mx-auto mb-2" />
              <span className="text-sm font-medium block text-center">{category.title}</span>
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="mb-6">
              <h2 className="text-2xl font-bodoni text-lapis-lazuli mb-2">
                {categories[activeCategory].title}
              </h2>
              {categories[activeCategory].subtitle && (
                <p className="text-sm text-gray-600 italic">{categories[activeCategory].subtitle}</p>
              )}
            </div>

            {activeCategory === 'laser' ? (
              renderLaserContent()
            ) : (
              renderSimpleTable(categories[activeCategory].items)
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PriceList;
